import { useEffect, useRef, useState } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { MAX_DEPTH } from './constants'
import { buildVisibleWindow, isEditableKeyboardTarget } from './helpers'

import * as S from './styles'

const CardStack = <T,>({
  items,
  currentIndex,
  onNext,
  onPrev,
  renderCard,
  getKey,
  animateTransitions = true
}: CardStackProps<T>) => {
  const { t } = useAPPTranslation()
  const [hasMounted, setHasMounted] = useState(false)
  const [swapDirection, setSwapDirection] = useState<1 | -1>(1)

  const handlePrev = () => {
    setSwapDirection(-1)
    onPrev()
  }

  const handleNext = () => {
    setSwapDirection(1)
    onNext()
  }

  const navigateRef = useRef({ handlePrev, handleNext, hasMultiple: false })
  navigateRef.current = {
    handlePrev,
    handleNext,
    hasMultiple: items.length > 1
  }

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!navigateRef.current.hasMultiple) {
        return
      }

      if (isEditableKeyboardTarget(event.target)) {
        return
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault()
        navigateRef.current.handlePrev()
        return
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault()
        navigateRef.current.handleNext()
      }
    }

    globalThis.addEventListener('keydown', onKeyDown)
    return () => globalThis.removeEventListener('keydown', onKeyDown)
  }, [])

  const hasMultipleItems = items.length > 1
  const visibleItems = buildVisibleWindow(items, currentIndex)

  return (
    <S.Stack data-testid="card-stack">
      {hasMultipleItems && (
        <S.ArrowButton
          data-testid="card-stack-prev-button"
          type="button"
          aria-label={t('c:actions.previous')}
          onClick={handlePrev}
        >
          <FiChevronLeft />
        </S.ArrowButton>
      )}

      <S.CardsWindow
        data-testid="card-stack-cards-window"
        data-animate={hasMounted && animateTransitions}
      >
        {visibleItems.map(({ item, offset }) => {
          const isFocused = offset === 0
          const depth = Math.min(Math.abs(offset), MAX_DEPTH)

          return (
            <S.Card
              data-testid="card-stack-card"
              key={getKey(item)}
              data-focused={isFocused}
              data-depth={depth}
              style={
                {
                  '--depth': depth,
                  '--swap-dir': swapDirection
                } as React.CSSProperties
              }
            >
              {renderCard(item, isFocused)}
            </S.Card>
          )
        })}
      </S.CardsWindow>

      {hasMultipleItems && (
        <S.ArrowButton
          data-testid="card-stack-next-button"
          type="button"
          aria-label={t('c:actions.next')}
          onClick={handleNext}
        >
          <FiChevronRight />
        </S.ArrowButton>
      )}
    </S.Stack>
  )
}

export default CardStack
